import { useEffect, useMemo, useState } from 'react';
import { formatModelCode } from '../utils/modelCategoryRanges';
import { roundRupees } from '../utils/money';
import { WHOLESALE_CATEGORY_LABELS } from '../utils/wholesaleOrderForm';

const RetailModelSidePanel = ({
  item,
  existingItem = null,
  existingSettledPrice = '',
  open,
  onClose,
  onConfirm,
  onClearSelection,
}) => {
  const [advance, setAdvance] = useState(0);
  const [settledPrice, setSettledPrice] = useState('');
  const [colorRequest, setColorRequest] = useState('');
  const [showColorNote, setShowColorNote] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setAdvance(existingItem ? roundRupees(existingItem.advance) : 0);
    setSettledPrice(
      existingSettledPrice !== '' &&
        existingSettledPrice !== null &&
        existingSettledPrice !== undefined &&
        Number.isFinite(Number(existingSettledPrice))
        ? roundRupees(Number(existingSettledPrice))
        : ''
    );
    setColorRequest(existingItem?.colorRequest ?? '');
    setShowColorNote(Boolean(existingItem?.colorRequest?.trim()));
  }, [open, item, existingItem, existingSettledPrice]);

  useEffect(() => {
    if (!open) return;

    document.body.classList.add('overflow-hidden');

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const catalogTotal = useMemo(
    () => (item ? roundRupees(item.price) : 0),
    [item]
  );

  const hasSettled =
    settledPrice !== '' &&
    settledPrice !== null &&
    settledPrice !== undefined &&
    Number.isFinite(Number(settledPrice));
  const effectiveTotal = hasSettled ? roundRupees(Number(settledPrice)) : catalogTotal;

  const advanceValue = roundRupees(advance);
  const balance = roundRupees(effectiveTotal - advanceValue);

  const settledError = useMemo(() => {
    if (!hasSettled) return null;
    if (Number(settledPrice) < 0) return 'Agreed price cannot be negative';
    return null;
  }, [hasSettled, settledPrice]);

  const advanceError = useMemo(() => {
    if (advanceValue < 0) return 'Advance cannot be negative';
    if (advanceValue > effectiveTotal) {
      return hasSettled
        ? 'Advance cannot exceed agreed price'
        : 'Advance cannot exceed model price';
    }
    return null;
  }, [advanceValue, effectiveTotal, hasSettled]);

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  const handleSettledChange = (raw) => {
    if (raw === '') {
      setSettledPrice('');
      return;
    }
    const parsed = parseFloat(raw);
    const next = Number.isNaN(parsed) ? 0 : parsed;
    setSettledPrice(next);
    const capped = roundRupees(Math.max(0, next));
    setAdvance((prev) => (roundRupees(prev) > capped ? capped : prev));
  };

  const handleSettledClear = () => {
    setSettledPrice('');
    setAdvance((prev) => {
      const current = roundRupees(prev);
      return current > catalogTotal ? catalogTotal : current;
    });
  };

  const handleAdvanceChange = (raw) => {
    const parsed = parseFloat(raw);
    setAdvance(Number.isNaN(parsed) ? 0 : parsed);
  };

  const handleConfirm = () => {
    if (advanceError || settledError) return;
    onConfirm({
      advance: advanceValue,
      colorRequest: colorRequest.trim(),
      settledPrice: hasSettled ? roundRupees(Number(settledPrice)) : '',
    });
  };

  if (!open || !item) return null;

  const isEditing = Boolean(existingItem);

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed top-0 right-0 h-full max-h-[100dvh] w-full sm:max-w-[90vw] sm:w-[36rem] bg-white shadow-2xl z-50 flex flex-col overflow-hidden md:left-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="retail-panel-title"
      >
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex justify-between items-start">
          <h2 id="retail-panel-title" className="text-lg sm:text-xl font-bold text-gray-800 pr-4 leading-tight">
            {isEditing ? 'Edit selection' : 'Select model'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl p-1 hover:bg-gray-100 rounded shrink-0"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex-1 min-h-0 min-h-[7rem] flex items-center justify-center bg-gray-50 rounded-lg overflow-hidden">
            {item.photo ? (
              <img
                src={item.photo}
                alt=""
                className="max-h-full max-w-full w-full object-contain p-2 sm:p-3"
              />
            ) : (
              <div className="w-full h-full min-h-[7rem] bg-gray-100 flex items-center justify-center text-gray-400 text-xs sm:text-sm">
                No photo
              </div>
            )}
          </div>

          <div className="shrink-0 min-w-0">
            <h3 className="text-base sm:text-xl font-bold text-gray-800 line-clamp-2">{item.modelName}</h3>
            <p className="text-sm text-gray-600">{formatModelCode(item.size, item.modelNumber)}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {WHOLESALE_CATEGORY_LABELS[item.size] || item.size || 'N/A'}
            </p>
            <p className="text-sm text-gray-700 mt-1.5 sm:mt-2">
              <span className="font-semibold text-green-700">{formatMoney(item.price)}</span>
              <span className="text-gray-400 mx-1.5">·</span>
              <span>Qty 1</span>
              <span className="text-gray-400 mx-1.5">·</span>
              <span>Stock {item.currentStock}</span>
            </p>
          </div>

          <div className="shrink-0 pt-1 border-t border-gray-100 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Agreed price <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  value={settledPrice === '' || settledPrice === null || settledPrice === undefined ? '' : settledPrice}
                  onChange={(e) => handleSettledChange(e.target.value)}
                  min="0"
                  step="1"
                  placeholder="Leave empty for original price"
                  className={`toolbar-input h-9 w-36 text-sm text-center px-2 focus:ring-green-500 focus:border-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${settledError ? 'border-red-500' : ''}`}
                  aria-label="Agreed price"
                />
                <button
                  type="button"
                  onClick={handleSettledClear}
                  className="admin-btn admin-btn-neutral h-9 px-2.5 text-xs"
                >
                  Clear
                </button>
              </div>
              {settledError ? (
                <p className="text-xs text-red-600 mt-1">{settledError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Original price {formatMoney(catalogTotal)}
                  {hasSettled ? ` · Agreed ${formatMoney(effectiveTotal)}` : ' · empty uses original price'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Advance payment</label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="number"
                  value={advance || ''}
                  onChange={(e) => handleAdvanceChange(e.target.value)}
                  min="0"
                  max={effectiveTotal}
                  step="1"
                  className={`toolbar-input h-9 w-28 text-sm text-center px-2 focus:ring-green-500 focus:border-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${advanceError ? 'border-red-500' : ''}`}
                  aria-label="Advance amount"
                />
                <button
                  type="button"
                  onClick={() => setAdvance(effectiveTotal)}
                  className="admin-btn admin-btn-success h-9 px-2.5 text-xs"
                >
                  Full amount
                </button>
                <button
                  type="button"
                  onClick={() => setAdvance(0)}
                  className="admin-btn admin-btn-neutral h-9 px-2.5 text-xs"
                >
                  Clear
                </button>
              </div>
              {advanceError ? (
                <p className="text-xs text-red-600 mt-1">{advanceError}</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">Max {formatMoney(effectiveTotal)}</p>
              )}
            </div>

            <div className="text-sm flex flex-wrap gap-x-4 gap-y-1">
              <div>
                <span className="text-gray-500">Original price </span>
                <span className="font-semibold text-gray-800">{formatMoney(catalogTotal)}</span>
              </div>
              <div>
                <span className="text-gray-500">Agreed </span>
                <span className="font-bold text-gray-900">{formatMoney(effectiveTotal)}</span>
              </div>
              <div>
                <span className="text-gray-500">Advance </span>
                <span className="font-semibold text-gray-800">{formatMoney(advanceValue)}</span>
              </div>
              <div className={balance === 0 ? 'text-green-700' : 'text-red-700'}>
                <span className="text-gray-500">Balance </span>
                <span className="font-semibold">{formatMoney(balance)}</span>
              </div>
            </div>
          </div>

          <div className="shrink-0">
            {showColorNote ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Color note</label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowColorNote(false);
                      setColorRequest('');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Remove
                  </button>
                </div>
                <textarea
                  value={colorRequest}
                  onChange={(e) => setColorRequest(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows={2}
                  placeholder="Special color requirements…"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowColorNote(true)}
                className="text-xs font-medium text-green-700 hover:text-green-900"
              >
                + Add color note
              </button>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t bg-white px-4 py-3 sm:px-6 sm:py-4 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={Boolean(advanceError || settledError)}
            className="admin-btn admin-btn-submit w-full py-2.5 sm:py-3 text-sm sm:text-base font-semibold disabled:opacity-50"
          >
            Confirm selection
          </button>
          {isEditing && (
            <button
              type="button"
              onClick={() => {
                onClearSelection();
                onClose();
              }}
              className="admin-btn admin-btn-danger w-full py-2.5 text-sm font-semibold"
            >
              Clear selection
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RetailModelSidePanel;
